import type { StrictOmit } from '@frontend/duck-tape';
import type { MouseEvent } from '@frontend/react';
import { Icon } from '@frontend/web-react';
import type { IconName } from '@frontend/web-react/components/Primitives/Icon/helpers';
import type { ClassName } from '@frontend/web-utils';
import { applyClass, concatClasses, safeCss } from '@frontend/web-utils';
import type { SidebarItemColor } from '@frontend/web/components/Navigation/SidebarItems/types';
import type { AnchorProps as MAnchorProps } from '@mantine/core';
import { Anchor as MAnchor } from '@mantine/core';
import type { HTMLAttributeAnchorTarget } from 'react';

type SidebarAnchorProps = StrictOmit<MAnchorProps, 'className' | 'color'> & {
  className?: ClassName;
  color?: SidebarItemColor;
  href: string;
  isCollapsed?: boolean;
  label: string;
  leftIconName?: IconName;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  rightIconName?: IconName;
  target?: HTMLAttributeAnchorTarget;
};

export const SidebarAnchor = ({
  className,
  color = 'primary',
  isCollapsed = false,
  label,
  leftIconName,
  onClick,
  rightIconName,
  target = '_blank',
  ...props
}: SidebarAnchorProps) => {
  return (
    <MAnchor
      classNames={{
        root: concatClasses(getRootClassName({ color, isCollapsed }), className),
      }}
      target={target}
      underline="never"
      {...props}
      onClick={onClick}
    >
      {leftIconName ? <Icon className={safeCss('mr-sm')} color="primary" name={leftIconName} /> : undefined}
      {isCollapsed ? null : <span className={getLabelClassName({ color })}>{label}</span>}
      {rightIconName ? <Icon color="primary" name={rightIconName} /> : undefined}
    </MAnchor>
  );
};

const getRootClassName = ({ isCollapsed }: PickRequired<SidebarAnchorProps, 'color' | 'isCollapsed'>) => {
  return concatClasses(
    safeCss('sidebar-hover-background-effect rounded-md py-sm px-md gap-x-xs no-underline flex flex-row items-center'),
    applyClass(isCollapsed, 'size-xxl'),
  );
};

const getLabelClassName = ({ color }: PickRequired<SidebarAnchorProps, 'color'>) => {
  const labelClassMap = {
    danger: safeCss('text-textError'),
    primary: safeCss('text-textPrimary'),
    secondary: safeCss('text-textSecondary'),
  };

  return concatClasses(safeCss('font-medium text-h6 leading-6 py-xs'), labelClassMap[color]);
};
